<script>
import ModelCreate from "../models/create-v2";
import FormsModel from "@/components/forms/sales-plan.vue";
import {serialize} from 'object-to-formdata';
import Swal from 'sweetalert2'
import Loader from "@/components/widgets/loader.vue";
import Alert from "@/components/alert.vue";
import {buildSalesPlanFormModel, buildSalesPlanPayload} from "@/services/transformers/salesPlanPayload";
import {clearSalesPlanDuplicateDraft, consumeSalesPlanDuplicateDraft} from "@/services/duplicate/salesPlanDraft";
import SalesPlanPublishConfirmation from "@/components/modals/sales-plan-publish-confirmation.vue";
import SalesPlanLoadErrorDialog from "@/components/modals/sales-plan-load-error-dialog.vue";

export default {
  components: {SalesPlanLoadErrorDialog, SalesPlanPublishConfirmation, Alert, Loader},
  extends: ModelCreate,
  page() {
    return {
      title: this.lodash.startCase(this.$t('create {something}', {something: this.$t('sales_plans.plural')})),
    };
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t('sales_plans.singular'))
      }
    },
    resourceRoute: {default: 'sales_plans'},
    modelVariable: {default: 'sales_plan'},
    form: {
      default() {
        return FormsModel;
      }
    },
    cardAttrs: {
      default() {
        return {
          class: 'sales-plan-card-form'
        }
      }
    }
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t('sales_plans.plural')),
      items: [
        {
          text: this.lodash.upperFirst(this.$t('sales_plans.plural')),
          href: "/sales_plans",
        },
        {
          text: this.$t("Create New"),
          href: "/sales_plans/create",
          active: true,
        },
      ],
      isValid: true,
      permissionChecked: false,
      duplicatePrefillReady: true,
      publishDisabled: true,
      failedStoreAction: null,
    }
  },
  mounted() {
    this.fetchCreatePermission()
  },
  methods: {
    async prefillDuplicateSalesPlan() {
      const duplicateDraft = consumeSalesPlanDuplicateDraft();

      if (!duplicateDraft) {
        this.duplicatePrefillReady = true;
        return;
      }

      try {
        this.$set(this, 'model', buildSalesPlanFormModel(duplicateDraft, duplicateDraft.processing ?? null));
        clearSalesPlanDuplicateDraft();
        this.duplicatePrefillReady = true;
      } catch (error) {
        this.duplicatePrefillReady = true;
        return Swal.fire({
          title: this.$t('Failed') + '!',
          text: (error.response && (error.response.data || {}).message) || error.message,
          type: 'danger',
          confirmButtonClass: 'btn btn-danger btn-fill',
          buttonsStyling: false
        }).then(() => this.$router.push('/sales_plans'))
      }
    },
    async fetchCreatePermission() {
      this.loading = true

      try {
        const response = await this.$axios.get('/v1/sales-plans/create')

        if (response?.status === 403) {
          return this.$router.replace('/403')
        }

        this.permissionChecked = true
        await this.prefillDuplicateSalesPlan()
      } catch (error) {
        if (error?.response?.status === 403) {
          return this.$router.replace('/403')
        }

        return Swal.fire({
          title: this.$t('Failed') + '!',
          text: (error.response && (error.response.data || {}).message) || error.message,
          type: 'danger',
          confirmButtonClass: 'btn btn-danger btn-fill',
          buttonsStyling: false
        }).then(() => this.$router.push('/sales_plans'))
      } finally {
        this.loading = false
      }
    },
    store(payload) {
      return this.$axios.post('/v1/sales-plans', serialize({...payload}, {indices: true, booleansAsIntegers: true}));
    },
    storeSuccessAlert(something, id) {
      return Swal.fire({
        title: this.$t('Success') + '!',
        text: this.$t(`You've added {something} with id {id}`, {something, id}),
        type: 'success',
        showConfirmButton: false,
        timer: 1500
      })
    },
    failedStoreModal(error, value, saveAsDraft) {
      const status = error?.response?.status;
      const isValidationError = status === 422;
      const retryable = !error?.response || status >= 500;
      const actionCopy = {
        draft: {title: 'sales_plans.modal_messages.save_draft_failed_title'},
        publish: {title: 'sales_plans.modal_messages.publish_failed_title'},
      }[saveAsDraft ? 'draft' : 'publish'];
      const fallbackMessage = this.$t('sales_plans.modal_messages.store_failed_message');

      if (isValidationError) {
        this.errors = error?.response?.data?.errors || {};
      }

      const description = status >= 500
        ? fallbackMessage
        : error?.response?.data?.message || error?.message || fallbackMessage;

      this.failedStoreAction = retryable ? {value, saveAsDraft} : null;
      this.$refs.salesPlanStoreErrorDialog.showMessage(
        this.$t(actionCopy.title),
        description,
        retryable
      );
    },
    retryFailedStore() {
      if (!this.failedStoreAction) return;

      const {value, saveAsDraft} = this.failedStoreAction;
      this.failedStoreAction = null;
      this.handleStore(value, saveAsDraft);
    },
    handleDraft(event) {
      event.preventDefault();
      const form = this.$refs.form;
      if (!form || typeof form.validateAll !== 'function') return;
      form.validateAll({skipEstimatedSaleLimit: true}).then((isValid) => {
        if (isValid) this.$nextTick(() => {
          this.handleStore(this.model, true)
        })
      })
    },
    handlePublish() {
      if (this.publishDisabled) return;
      const form = this.$refs.form;
      if (!form || typeof form.validateAll !== 'function') return;
      form.validateAll().then((isValid) => {
        if (isValid) this.$nextTick(() => {
          this.handleStore(this.model)
        })
      })
    },
    handleStore(value, saveAsDraft = false) {
      this.storing = true;
      this.failedStoreAction = null;
      this.store(buildSalesPlanPayload(value, {saveAsDraft}))
          .then((res) => {
            const {data: {data: {id}}} = res
            this.isEditing = false;
            return this.$router.push({name: 'sales_plans.show', params: {key: id}})
          })
          .catch((error) => this.failedStoreModal(error, value, saveAsDraft))
          .finally(() => {
            this.storing = false
          })
    },
    showPublishConfirmationModal() {
      const modalRef = this.$refs["sales-plan-publish-confirmation"]
      const title = this.$t('sales_plans.modal_messages.confirm_publish_title')
      const message = this.$t('sales_plans.modal_messages.confirm_publish_message')
      if (!modalRef) {
        return Swal.fire({
          title: title + "!",
          text: message,
          type: "success",
          confirmButtonClass: "btn btn-success btn-fill",
          buttonsStyling: false
        });
      }

      return modalRef.show({
        title: title,
        message,
      });
    }
  },
}
</script>

<template>
  <Layout>
    <div class="row">
      <Loader :loading="loading" no-axios>
        <div v-if="permissionChecked && duplicatePrefillReady">
          <PageHeader :items="items" :title="title"/>
          <div>
            <div class="row">
              <template v-for="(alert, index) in alerts">
                <div :key="index" class="col-12 form-row">
                  <div class="col-md-8"></div>
                  <div class="col">
                    <Alert v-bind="alert"></Alert>
                  </div>
                </div>
              </template>
            </div>
            <b-card :sub-title="undefined" :title="undefined" class="card-form" v-bind="cardAttrs">
              <b-card-title class="clearfix" title-tag="div">
                <slot name="card-header">
                  <b-card-title class="text-primary">{{
                      cardAttrs.title || `${$t('Create')} ${cardTitle || lodash.startCase($t(resourceRoute + '.singular'))}`
                    }}
                  </b-card-title>
                  <b-card-sub-title v-if="cardAttrs.subTitle">{{ cardAttrs.subTitle }}</b-card-sub-title>
                </slot>
              </b-card-title>
              <slot>
                <component
                    :is="form"
                    ref="form"
                    v-model="model"
                    :errors="errors"
                    :is-valid.sync="isValid"
                    :publish-disabled.sync="publishDisabled"
                    @update:errors="$emit('update:errors', $event)"
                    @model-changed="$emit('model-changed',$event)"
                >
                </component>
              </slot>
              <slot name="card-footer" v-bind="{ loading, storing, handleStore, handleBack }">
                <div class="row footer-wrapper">
                  <div class="col-12 d-flex flex-row-reverse justify-content-start flex-wrap">
                    <b-button :disabled="storing || publishDisabled" class="ms-2" native-type="submit" variant="primary"
                              @click="showPublishConfirmationModal">
                      <i v-if="storing" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                      {{ lodash.startCase($t('publish')) }}
                    </b-button>
                    <b-button :disabled="storing" class="ms-2" native-type="submit" variant="warning"
                              @click="handleDraft">
                      <i v-if="storing" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                      {{ lodash.startCase($t('sales_plans.buttons.save_as_draft')) }}
                    </b-button>
                    <b-button class="ms-2" variant="link" @click="handleBack">
                      {{ $t('Back') }}
                    </b-button>
                  </div>
                </div>
              </slot>
            </b-card>
          </div>
        </div>
        <sales-plan-publish-confirmation
            ref="sales-plan-publish-confirmation"
            @confirm="handlePublish"
        />
        <sales-plan-load-error-dialog
            :close-only="true"
            ref="salesPlanStoreErrorDialog"
            @retry="retryFailedStore"
        />
      </Loader>
    </div>
  </Layout>
</template>
